// Bridges the signed-in identity to the local "player name" the game already
// uses. There is no profile backend yet (Supabase tables are a planned
// follow-up — see DEV-AUTH.md), so "syncing" here means: when you sign in, your
// account's display name becomes your in-game name. Because local stats key on
// that name (src/shared/stats.ts), your matches then accrue under your account
// identity too.
import type { User } from "@supabase/supabase-js";
import { loadLocalPlayer, saveLocalPlayer, defaultLocalPlayer } from "./local-players";

// Remembers which account the local name was last adopted from, so adoption
// happens exactly once per account and later manual edits survive a reload.
const SYNCED_FROM_KEY = "ar.name.syncedFrom";

// In-game names are capped at 16 chars (see the lobby/offline name inputs).
const MAX_NAME = 16;

// Prefer a human name, fall back to the email's local part, then a generic.
export function resolveDisplayName(user: User | null | undefined): string {
  const meta: any = user?.user_metadata || {};
  const name =
    meta.full_name ||
    meta.name ||
    meta.user_name ||
    (user?.email ? user.email.split("@")[0] : "Player");
  return String(name).substring(0, MAX_NAME);
}

// Adopt the signed-in account's display name as player 1's name, but only once
// per account: if the user later edits the name, that edit is preserved across
// reloads (we don't re-adopt for an account we've already synced). Returns the
// name now stored for player 1.
export function syncAccountNameToLocal(user: User | null | undefined): string {
  if (!user) return loadLocalPlayer(0)?.name ?? "";

  let syncedFrom = "";
  try {
    syncedFrom = localStorage.getItem(SYNCED_FROM_KEY) || "";
  } catch {
    /* private mode / no storage — just fall through to adopt */
  }

  if (syncedFrom !== user.id) {
    const name = resolveDisplayName(user);
    // Adopt the account name as player 1's name, preserving their color/keys.
    const p1 = loadLocalPlayer(0) ?? defaultLocalPlayer(0, name);
    saveLocalPlayer(0, { ...p1, name });
    try {
      localStorage.setItem(SYNCED_FROM_KEY, user.id);
    } catch {
      /* ignore */
    }
    return name;
  }

  return loadLocalPlayer(0)?.name ?? resolveDisplayName(user);
}

// On sign-out, forget which account we synced from so a future (possibly
// different) sign-in re-adopts that account's name. We intentionally leave the
// stored name in place so the signed-out experience is unchanged.
export function clearAccountNameSync(): void {
  try {
    localStorage.removeItem(SYNCED_FROM_KEY);
  } catch {
    /* ignore */
  }
}
